import { useState } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectIsAuthenticated } from "@/store/slices/authSlice";
import {
  selectHasSeenEntranceAnimation,
  setEntranceAnimationSeen,
  setGdprConsent,
} from "@/store/slices/onboardingSlice";

/**
 * Home ViewModel State
 *
 * Tracks the entrance animation and consent flow state.
 */
export interface HomeViewModelState {
  /** Whether to show the entrance animation */
  showAnimation: boolean;
  /** Whether the user has accepted the consent terms */
  consentAccepted: boolean;
  /** Whether the animation sequence has completed */
  animationComplete: boolean;
}

/**
 * Home ViewModel Actions
 *
 * All user intents for the home page.
 */
export interface HomeViewModelActions {
  /** Called when the entrance animation completes */
  onAnimationComplete: () => void;
  /** Called when the consent checkbox state changes */
  onConsentChange: (accepted: boolean) => void;
  /** Navigate to signup page (only if consent accepted) */
  goToSignup: () => void;
}

/**
 * Home ViewModel Return Type
 */
export interface HomeViewModel {
  state: HomeViewModelState;
  actions: HomeViewModelActions;
  onMount: () => void;
}

/**
 * useHomeViewModel Hook
 *
 * MVVM ViewModel for the HomePage.
 * Handles entrance animation logic and navigation based on auth state.
 *
 * **Business Rules:**
 * 1. If authenticated -> redirect to /owner/trophy-room
 * 2. If hasSeenEntranceAnimation -> skip animation (show final state)
 * 3. On animation complete -> dispatch setEntranceAnimationSeen()
 * 4. Sign Up button -> disabled until consentAccepted === true
 * 5. On Sign Up click -> navigate to /auth/signup
 *
 * @returns ViewModel with { state, actions, onMount }
 */
export function useHomeViewModel(): HomeViewModel {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const hasSeenAnimation = useAppSelector(selectHasSeenEntranceAnimation);

  // Local state
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);

  // State object
  const state: HomeViewModelState = {
    showAnimation: !hasSeenAnimation && !isAuthenticated,
    consentAccepted,
    animationComplete,
  };

  // Actions object
  // Note: React Compiler automatically memoizes these functions
  const actions: HomeViewModelActions = {
    onAnimationComplete: () => {
      setAnimationComplete(true);
      dispatch(setEntranceAnimationSeen());
    },

    onConsentChange: (accepted: boolean) => {
      setConsentAccepted(accepted);
      // Persist consent state for use on signup page
      dispatch(setGdprConsent(accepted));
    },

    goToSignup: () => {
      if (consentAccepted) {
        navigate("/auth/signup");
      }
    },
  };

  // Lifecycle - called on mount
  // Note: React Compiler automatically memoizes this function
  const onMount = () => {
    // If authenticated, redirect to trophy room
    if (isAuthenticated) {
      navigate("/owner/trophy-room");
      return;
    }

    // If animation has been seen before, mark as complete immediately
    if (hasSeenAnimation) {
      setAnimationComplete(true);
    }
  };

  return { state, actions, onMount };
}
